import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditRecipePage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(null);
  const [regenerateImage, setRegenerateImage] = useState(false); // NOVO: opção de regenerar imagem
  const [imagePrompt, setImagePrompt] = useState(''); // NOVO: detalhes da imagem

  useEffect(() => {
    fetchCategories();
    fetchRecipe();
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchRecipe = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/recipes/${id}`);
      const recipe = response.data;
      setFormData({
        title: recipe.title,
        description: recipe.description,
        category: recipe.category,
        servings: recipe.servings,
        prep_time: recipe.prep_time || 0,
        cook_time: recipe.cook_time || 0,
        ingredients: recipe.ingredients,
        instructions: recipe.instructions
      });
    } catch (error) {
      console.error('Error fetching recipe:', error);
      toast.error(t('recipe.errorLoadingRecipes'));
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index][field] = value;
    setFormData(prev => ({ ...prev, ingredients: newIngredients }));
  };

  const addIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: '', quantity: '', unit: '' }]
    }));
  };

  const removeIngredient = (index) => {
    if (formData.ingredients.length > 1) {
      setFormData(prev => ({
        ...prev,
        ingredients: prev.ingredients.filter((_, i) => i !== index)
      }));
    }
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = value;
    setFormData(prev => ({ ...prev, instructions: newInstructions }));
  };

  const addInstruction = () => {
    setFormData(prev => ({
      ...prev,
      instructions: [...prev.instructions, '']
    }));
  };

  const removeInstruction = (index) => {
    if (formData.instructions.length > 1) {
      setFormData(prev => ({
        ...prev,
        instructions: prev.instructions.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      toast.error(t('recipe.errorSavingRecipe'));
      return;
    }
    if (!formData.description.trim()) {
      toast.error(t('recipe.errorSavingRecipe'));
      return;
    }
    if (formData.ingredients.some(ing => !ing.name.trim() || !ing.quantity.trim())) {
      toast.error(t('recipe.errorSavingRecipe'));
      return;
    }
    if (formData.instructions.some(inst => !inst.trim())) {
      toast.error(t('recipe.errorSavingRecipe'));
      return;
    }

    try {
      setSaving(true);
      
      if (regenerateImage) {
        toast.info(t('recipe.savingAndRegenerating'));
        
        // Salvar receita e regenerar imagem
        await axios.put(`${API}/recipes/${id}`, {
          ...formData,
          regenerate_image: true,
          image_prompt: imagePrompt  // Detalhes para a imagem
        });
      } else {
        toast.info(t('recipe.savingRecipe'));
        await axios.put(`${API}/recipes/${id}`, formData);
      }
      
      toast.success(t('recipe.recipeSaved'));
      navigate(`/recipe/${id}`);
    } catch (error) {
      console.error('Error updating recipe:', error);
      toast.error(t('recipe.errorSavingRecipe'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !formData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">Carregando receita...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button 
            data-testid="back-button"
            onClick={() => navigate(`/recipe/${id}`)} 
            variant="ghost"
            disabled={saving}
            className="hover:bg-orange-50"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Voltar
          </Button>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-amber-900 mb-2">Editar Receita</h1>
          <p className="text-lg text-gray-600">A IA vai regenerar a imagem se necessário</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card className="p-6 border-2 border-amber-100">
            <h2 className="text-2xl font-semibold text-amber-900 mb-6">Informações Básicas</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="title" className="text-base font-medium">Título da Receita</Label>
                <Input
                  data-testid="recipe-title-input"
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="mt-2 text-base"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-base font-medium">Descrição</Label>
                <Textarea
                  data-testid="recipe-description-input"
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="mt-2 min-h-24 text-base"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="category" className="text-base font-medium">Categoria</Label>
                  <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                    <SelectTrigger data-testid="category-select" className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="servings" className="text-base font-medium">Porções</Label>
                  <Input
                    data-testid="servings-input"
                    id="servings"
                    type="number"
                    min="1"
                    value={formData.servings}
                    onChange={(e) => handleInputChange('servings', parseInt(e.target.value) || 1)}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="prep_time" className="text-base font-medium">Tempo (min)</Label>
                  <Input
                    data-testid="prep-time-input"
                    id="prep_time"
                    type="number"
                    min="0"
                    value={formData.prep_time + formData.cook_time}
                    onChange={(e) => handleInputChange('prep_time', parseInt(e.target.value) || 0)}
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Ingredients */}
          <Card className="p-6 border-2 border-amber-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-amber-900">Ingredientes</h2>
              <Button 
                data-testid="add-ingredient-button"
                type="button"
                onClick={addIngredient}
                variant="outline"
                size="sm"
                className="border-orange-300 hover:bg-orange-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar
              </Button>
            </div>

            <div className="space-y-3">
              {formData.ingredients.map((ingredient, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    data-testid={`ingredient-name-${index}`}
                    type="text"
                    placeholder="Nome"
                    value={ingredient.name}
                    onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    data-testid={`ingredient-quantity-${index}`}
                    type="text"
                    placeholder="Qtd"
                    value={ingredient.quantity}
                    onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                    className="w-24"
                  />
                  <Input
                    data-testid={`ingredient-unit-${index}`}
                    type="text"
                    placeholder="Unidade"
                    value={ingredient.unit}
                    onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                    className="w-32"
                  />
                  {formData.ingredients.length > 1 && (
                    <Button
                      data-testid={`remove-ingredient-${index}`}
                      type="button"
                      onClick={() => removeIngredient(index)}
                      variant="outline"
                      size="icon"
                      className="border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Instructions */}
          <Card className="p-6 border-2 border-amber-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-amber-900">Modo de Preparo</h2>
              <Button 
                data-testid="add-instruction-button"
                type="button"
                onClick={addInstruction}
                variant="outline"
                size="sm"
                className="border-orange-300 hover:bg-orange-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Passo
              </Button>
            </div>

            <div className="space-y-3">
              {formData.instructions.map((instruction, index) => (
                <div key={index} className="flex gap-2">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm mt-2">
                    {index + 1}
                  </div>
                  <Textarea
                    data-testid={`instruction-${index}`}
                    value={instruction}
                    onChange={(e) => handleInstructionChange(index, e.target.value)}
                    className="flex-1 min-h-20"
                  />
                  {formData.instructions.length > 1 && (
                    <Button
                      data-testid={`remove-instruction-${index}`}
                      type="button"
                      onClick={() => removeInstruction(index)}
                      variant="outline"
                      size="icon"
                      className="border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row justify-end gap-4 mt-8">
            {/* Opções de Imagem */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  id="regenerateImage"
                  checked={regenerateImage}
                  onChange={(e) => {
                    setRegenerateImage(e.target.checked);
                    if (!e.target.checked) setImagePrompt(''); // Limpar prompt se desmarcar
                  }}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <label htmlFor="regenerateImage" className="text-sm text-gray-700 cursor-pointer font-medium">
                  🎨 Regenerar imagem com IA
                </label>
              </div>
              
              {/* Campo de detalhes - aparece só se checkbox marcado */}
              {regenerateImage && (
                <div className="ml-6">
                  <Input
                    type="text"
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    placeholder="Ex: vista de cima, fundo branco, prato colorido..."
                    className="text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Opcional: Descreva como quer a imagem
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-4">
              <Button 
                type="button"
                onClick={() => navigate(`/recipe/${id}`)}
                variant="outline"
                disabled={saving}
                className="px-8"
              >
                Cancelar
              </Button>
              <Button 
                data-testid="save-recipe-button"
                type="submit"
                disabled={saving}
                className="bg-orange-600 hover:bg-orange-700 text-white px-8"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {regenerateImage ? 'Salvando e gerando imagem...' : 'Salvando...'}
                  </>
                ) : (
                  'Salvar Alterações'
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRecipePage;